import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import aviso from '../lib/aviso';
import { FaWhatsapp, FaInstagram } from 'react-icons/fa';
import {
    LuPlus, LuSearch, LuEye, LuTrash2, LuChevronDown, LuChevronUp, LuChevronsUpDown,
    LuSmartphone, LuMegaphone, LuPlug, LuRefreshCw,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import { ListRow, RowAction, RowActions, RowConfirm, FiltroPill, ListCard, ListCards, CardConfirm } from '../components/ListKit';
import '../components/ListKit.css';
import { useRecorte } from '../hooks/useRecorte';
import { useCelular } from '../hooks/useCelular';
import { useConectarWhatsappMeta } from '../hooks/useConectarWhatsappMeta';
import { MetaService, type MetaConfig, type StatusWhatsapp } from '../services/MetaService';
import {
    ConexaoPlataformaService, getPlatformLabel, PlataformaEnum, StatusConexaoEnum, type ConexaoPlataforma,
} from '../services/ConexaoPlataformaService';
import { EvolutionService } from '../services/EvolutionService';
import { RegrasIAService, type AgenteListItem } from '../services/RegrasIAService';
import { InstagramService, INSTAGRAM_AUTH_MESSAGE } from '../services/InstagramService';
import { INSTAGRAM_STATE_KEY } from './InstagramCallbackPage';
import { TRAFEGO_PAGO_STATE_KEY } from './TrafegoPagoCallbackPage';
import { TrafegoPagoService, TRAFEGO_PAGO_AUTH_MESSAGE, type ContaAnuncio } from '../services/TrafegoPagoService';
import { apiFetch } from '../services/api';
import { temFuncionalidade, ehAdministradorDaEmpresa, temPermissao, PERMISSOES } from '../config/permissions';
import ConexaoPanel, { type RascunhoConexao, type UsuarioDaEmpresa } from '../components/conexoes/ConexaoPanel';
import ConectarMetaPanel from '../components/conexoes/ConectarMetaPanel';
import ConectarQrPanel from '../components/conexoes/ConectarQrPanel';
import RdStationPanel from '../components/apps/RdStationPanel';
import WebhookEntradaPanel from '../components/apps/WebhookEntradaPanel';
import { RdStationService } from '../services/RdStationService';
import { WebhookEntradaService } from '../services/WebhookEntradaService';
import CanalIcone from '../components/conexoes/CanalIcone';
import {
    DIAS_AVISO_EXPIRACAO, classeStatus, diasAte, formatarData, identificadorVisivel, renovaSozinha, rotuloStatus, temAtendimento,
} from '../components/conexoes/canais';
import './ConnectionsPage.css';

interface RespostaUsuarios { sucesso?: boolean; dados?: { userId: number; userName: string }[] }
interface RespostaAgentes { sucesso?: boolean; dados?: AgenteListItem[] }

/* Conexões — Direção A do desenho fechado de 30/09/2026
   (docs/designsystem/propostas/conexoes.html). Prefixo de CSS: `cx-`.

   O molde é o das outras telas de Configurações: PageHeader com abas, ListKit,
   RecordPanel. O que era aba (WhatsApp, Instagram, Anúncios) virou entrada do
   menu "Conectar canal"; o que era modal virou painel. O multi-contas saiu
   daqui — mora em Minhas empresas › Parâmetros — e esta tela só lê o valor. */

const API_URL = import.meta.env.VITE_API_URL || '';

type Aba = 'canais' | 'anuncios';
type Ordem = { coluna: 'canal' | 'expira'; direcao: 'asc' | 'desc' } | null;
type Painel =
    | { tipo: 'conexao'; conexao: ConexaoPlataforma; modo: 'ver' | 'editar' }
    | { tipo: 'conta'; conta: ContaAnuncio }
    | { tipo: 'meta' }
    | { tipo: 'qr'; conexaoId?: number; nome?: string }
    | { tipo: 'rd' }
    | { tipo: 'webhook'; id: number }
    | null;

/* Os quatro estados de sincronização da conta de anúncio — os mesmos da tela de Anúncios. */
const STATUS_SINCRONIZACAO: Record<string, { rotulo: string; classe: string; dica: string }> = {
    Ativa: { rotulo: 'Sincronizando', classe: 'is-ok', dica: 'Os dados de gasto e desempenho estão sendo atualizados.' },
    Pausada: { rotulo: 'Pausada', classe: 'is-off', dica: 'A sincronização desta conta está pausada.' },
    ErroAutenticacao: { rotulo: 'Reconectar', classe: 'is-warn', dica: 'O acesso expirou. Refaça a conexão com a Meta.' },
    ErroPermissao: { rotulo: 'Sem permissão', classe: 'is-bad', dica: 'Verifique o acesso a esta conta no Business Manager.' },
};

const relativo = (iso: string | null): string => {
    if (!iso) return 'nunca';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'nunca';
    const min = Math.floor((Date.now() - d.getTime()) / 60_000);
    if (min < 1) return 'agora mesmo';
    if (min < 60) return `há ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${h}h`;
    const dias = Math.floor(h / 24);
    return dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
};

const COLUNAS_CANAIS = 'minmax(0,1.7fr) 1fr 1fr 120px 100px 78px';
const COLUNAS_CONTAS = 'minmax(0,1.7fr) 100px 1fr 1fr 130px 78px';

/* No celular não há tooltip: o aviso de vencimento vira texto na própria linha. */
const textoExpiracao = (iso: string | null | undefined, dias: number | null, avisa: boolean): string | null => {
    if (!iso) return null;
    if (!avisa || dias === null) return `expira ${formatarData(iso)}`;
    if (dias < 0) return 'token vencido';
    if (dias === 0) return 'vence hoje';
    return dias === 1 ? 'vence amanhã' : `vence em ${dias} dias`;
};

/* O que se perde ao remover: no webhook de entrada são leads, não mensagens. */
const perguntaRemover = (c: ConexaoPlataforma) => c.plataforma === PlataformaEnum.Webhook
    ? `Apagar ${c.nome}? O endereço para de funcionar e os leads dessa fonte deixam de entrar.`
    : `Remover ${c.nome}? As mensagens desse canal deixam de entrar no CRM.`;

const ConnectionsPage = () => {
    const admin = ehAdministradorDaEmpresa();
    const navigate = useNavigate();
    /* A tela Apps manda para cá depois de conectar: `?abrir=rd` abre o painel do RD e
       `?aba=anuncios` cai nas contas de anúncio. Lido uma vez e apagado da barra. */
    const [params, setParams] = useSearchParams();
    const celular = useCelular();
    /* Sistema › Funcionalidades: desligada, a forma de conectar SOME do menu e do
       aviso de lista vazia (decisão dele em 30/09). O que já está conectado continua
       na lista, com o remover, para o cliente conseguir desligar. */
    const flag = {
        instagram: temFuncionalidade('instagram'),
        /* As rotas de conta de anúncio pedem a chave `anuncios` na API: sem ela,
           nem busca — senão o 403 levantaria o aviso global ao abrir a tela. */
        anuncios: temFuncionalidade('anuncios') && temPermissao(PERMISSOES.anuncios),
        leadAds: temFuncionalidade('lead_ads'),
        qrCode: temFuncionalidade('whatsapp_evolution'),
    };

    /* ── recorte de trabalho ── */
    const [recorte, gravarRecorte] = useRecorte<{ aba: Aba; busca: string; plataforma: string | null; status: string | null }>(
        'cx:recorte', { aba: 'canais', busca: '', plataforma: null, status: null });
    const [aba, setAba] = useState<Aba>(recorte.aba);
    const [busca, setBusca] = useState(recorte.busca);
    const [fPlataforma, setFPlataforma] = useState<string | null>(recorte.plataforma);
    const [fStatus, setFStatus] = useState<string | null>(recorte.status);
    useEffect(() => { gravarRecorte({ aba, busca, plataforma: fPlataforma, status: fStatus }); },
        [aba, busca, fPlataforma, fStatus, gravarRecorte]);
    const [ordem, setOrdem] = useState<Ordem>(null);

    /* ── dados ── */
    const [carregando, setCarregando] = useState(true);
    const [conexoes, setConexoes] = useState<ConexaoPlataforma[]>([]);
    const [contas, setContas] = useState<ContaAnuncio[]>([]);
    const [statusWa, setStatusWa] = useState<StatusWhatsapp>({ multiWhatsappHabilitado: false });
    const [metaConfig, setMetaConfig] = useState<MetaConfig | null>(null);
    const [adsConfig, setAdsConfig] = useState<MetaConfig | null>(null);

    /* ── painéis e ações ── */
    const [painel, setPainel] = useState<Painel>(null);
    const [menuAberto, setMenuAberto] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [aConfirmar, setAConfirmar] = useState<{ tipo: 'conexao' | 'conta'; id: number } | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [conectandoIg, setConectandoIg] = useState(false);
    const [conectandoAds, setConectandoAds] = useState(false);
    const [usuarios, setUsuarios] = useState<UsuarioDaEmpresa[]>([]);
    const [agentes, setAgentes] = useState<AgenteListItem[]>([]);

    useEffect(() => {
        const abrir = params.get('abrir');
        const abaPedida = params.get('aba');
        if (!abrir && !abaPedida) return;
        if (abaPedida === 'anuncios' || abaPedida === 'canais') setAba(abaPedida);
        if (abrir === 'rd') { setAba('canais'); setPainel({ tipo: 'rd' }); }
        /* `?abrir=webhook:<id>`: a entrada que a tela Apps acabou de criar. */
        const idWebhook = abrir?.startsWith('webhook:') ? Number(abrir.slice(8)) : NaN;
        if (Number.isInteger(idWebhook) && idWebhook > 0) { setAba('canais'); setPainel({ tipo: 'webhook', id: idWebhook }); }
        setParams({}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [carregandoOpcoes, setCarregandoOpcoes] = useState(false);

    const carregarConexoes = useCallback(async () => {
        try {
            const r = await ConexaoPlataformaService.buscarConexoesAtivas();
            if (r.sucesso) setConexoes(r.dados || []);
        } catch { /* a lista fica como está */ }
    }, []);

    const carregarContas = useCallback(async () => {
        if (!flag.anuncios) return;
        try { setContas(await TrafegoPagoService.buscarContas()); } catch { /* idem */ }
        // `flag` vem do usuário logado e não muda com a tela aberta.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const carregarStatus = useCallback(async () => {
        const r = await MetaService.getWhatsappStatus();
        if (r.sucesso && r.dados) setStatusWa(r.dados);
    }, []);

    useEffect(() => {
        (async () => {
            setCarregando(true);
            try {
                const [, , , metaCfg, adsCfg] = await Promise.all([
                    carregarConexoes(),
                    carregarContas(),
                    carregarStatus(),
                    MetaService.getMetaConfig(),
                    flag.anuncios ? TrafegoPagoService.getMetaConfig('') : Promise.resolve(null),
                ]);
                if (metaCfg.sucesso && metaCfg.dados) setMetaConfig(metaCfg.dados);
                if (adsCfg?.sucesso && adsCfg.dados?.configurationId) setAdsConfig(adsCfg.dados);
            } finally {
                setCarregando(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carregarConexoes, carregarContas, carregarStatus]);

    /* Opções de edição (usuários e agentes) só quando o painel abre em edição. */
    const modoPainel = painel?.tipo === 'conexao' ? painel.modo : null;
    useEffect(() => {
        if (modoPainel !== 'editar') return;
        let vivo = true;
        (async () => {
            setCarregandoOpcoes(true);
            try {
                const [ag, us] = await Promise.all([
                    RegrasIAService.getAgentes() as Promise<RespostaAgentes>,
                    admin && statusWa.multiWhatsappHabilitado
                        ? apiFetch(`${API_URL}/api/Usuario/BuscarTodos`).then(r => r.json() as Promise<RespostaUsuarios>)
                        : Promise.resolve<RespostaUsuarios | null>(null),
                ]);
                if (!vivo) return;
                if (ag?.sucesso) setAgentes(ag.dados || []);
                if (us?.sucesso && Array.isArray(us.dados)) {
                    setUsuarios(us.dados.map(u => ({ id: u.userId, name: u.userName })));
                }
            } catch { /* o painel funciona só com nome e status */ }
            finally { if (vivo) setCarregandoOpcoes(false); }
        })();
        return () => { vivo = false; };
    }, [modoPainel, admin, statusWa.multiWhatsappHabilitado]);

    /* Os painéis do RD e do webhook de entrada escolhem um responsável fixo entre os usuários da empresa. */
    const rdAberto = painel?.tipo === 'rd' || painel?.tipo === 'webhook';
    useEffect(() => {
        if (!rdAberto || !admin || usuarios.length) return;
        apiFetch(`${API_URL}/api/Usuario/BuscarTodos`)
            .then(r => r.json() as Promise<RespostaUsuarios>)
            .then(us => { if (us?.sucesso && Array.isArray(us.dados)) setUsuarios(us.dados.map(u => ({ id: u.userId, name: u.userName }))); })
            .catch(() => null);
    }, [rdAberto, admin, usuarios.length]);

    /* Agentes também no modo ver, para o nome do agente padrão. */
    useEffect(() => {
        if (agentes.length) return;
        RegrasIAService.getAgentes().then(r => { if (r?.sucesso) setAgentes(r.dados || []); }).catch(() => null);
    }, [agentes.length]);

    /* ── menu "Conectar canal": fecha ao clicar fora ou Esc ── */
    useEffect(() => {
        if (!menuAberto) return;
        const fora = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuAberto(false); };
        const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuAberto(false); };
        document.addEventListener('mousedown', fora);
        document.addEventListener('keydown', esc);
        return () => { document.removeEventListener('mousedown', fora); document.removeEventListener('keydown', esc); };
    }, [menuAberto]);

    /* ── uso do plano por plataforma (etapa 4: a API passou a devolver) ── */
    const limite = statusWa.limiteConexaoPorPlataforma ?? null;
    const usadas = (p: number) => statusWa.conexoesPorPlataforma?.[String(p)]
        ?? conexoes.filter(c => c.plataforma === p).length;
    const noLimite = (p: number) => limite !== null && usadas(p) >= limite;
    const usoTexto = (p: number) => limite === null ? undefined : `${usadas(p)} de ${limite} do plano em uso`;

    /* ── WhatsApp = Meta + Evolution SOMADOS (docs/ConfigurarWhatsapp D2/D15) ──
       O backend já calcula o limite somado e o motivo do bloqueio; o front só lê,
       com fallback para uma API antiga que não manda os campos novos. */
    const limiteWa = statusWa.limiteWhatsapp ?? limite;
    const usadasWa = statusWa.whatsappsConectados
        ?? (usadas(PlataformaEnum.WhatsappMeta) + usadas(PlataformaEnum.WhatsappEvolution));
    const podeWhatsapp = statusWa.podeConectarWhatsapp ?? (limiteWa === null || usadasWa < limiteWa);
    const bloqueioWa = podeWhatsapp ? null : (statusWa.motivoBloqueioWhatsapp || 'Limite do plano atingido');
    const usoTextoWa = limiteWa === null ? undefined : `${usadasWa} de ${limiteWa} WhatsApp do plano em uso`;

    /* ── Meta: SDK do Facebook (o appId é o mesmo do WhatsApp e dos anúncios). O fluxo mora em
       `useConectarWhatsappMeta`, que o Chat sem número também usa. ── */
    const fbAppId = metaConfig?.appId ?? adsConfig?.appId ?? null;
    const { pronto: fbPronto, conectando: conectandoMeta, conectar: conectarMeta } = useConectarWhatsappMeta({
        appId: fbAppId,
        configurationId: metaConfig?.configurationId,
        aoConectar: async () => {
            setPainel(null);
            await Promise.all([carregarConexoes(), carregarStatus()]);
        },
    });

    /* ── Instagram e Anúncios: popup próprio (redirect_uri registrado), como antes ──
       O Instagram conecta só pelo login do próprio Instagram (docs/instagram-login/especificacao.md);
       a conexão pela Página do Facebook foi retirada. */
    const abrirPopup = useCallback(async (qual: 'instagram' | 'anuncios') => {
        const ehInstagram = qual === 'instagram';
        const setBusy = ehInstagram ? setConectandoIg : setConectandoAds;
        setBusy(true);
        try {
            const state = crypto.randomUUID();
            sessionStorage.setItem(ehInstagram ? INSTAGRAM_STATE_KEY : TRAFEGO_PAGO_STATE_KEY, state);
            const cfg = ehInstagram
                ? await InstagramService.getConfiguracaoLoginInstagram(state)
                : await TrafegoPagoService.getMetaConfig(state);
            if (!cfg.sucesso || !cfg.dados?.urlAutorizacao) {
                aviso.erro(cfg.mensagem || 'Configuração da Meta não carregada.');
                setBusy(false);
                return;
            }
            const popup = window.open(cfg.dados.urlAutorizacao, `${ehInstagram ? 'instagram' : qual}-oauth`, 'width=600,height=750,menubar=no,toolbar=no');
            if (!popup) { aviso.atencao('Permita pop-ups para este site e tente novamente.'); setBusy(false); }
        } catch {
            aviso.erro('Não foi possível iniciar a conexão.');
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        const aoReceber = async (e: MessageEvent) => {
            if (e.origin !== window.location.origin) return;
            if (e.data?.type === INSTAGRAM_AUTH_MESSAGE) {
                setConectandoIg(false);
                if (e.data.success) { aviso.ok('Instagram conectado.'); await Promise.all([carregarConexoes(), carregarStatus()]); }
                else aviso.erro(e.data.mensagem || 'Não foi possível conectar o Instagram.');
            }
            if (e.data?.type === TRAFEGO_PAGO_AUTH_MESSAGE) {
                setConectandoAds(false);
                if (e.data.success) { aviso.ok(e.data.mensagem || 'Conta de anúncios conectada.'); await carregarContas(); setAba('anuncios'); }
                else aviso.erro(e.data.mensagem || 'Não foi possível conectar a conta de anúncios.');
            }
        };
        window.addEventListener('message', aoReceber);
        return () => window.removeEventListener('message', aoReceber);
    }, [carregarConexoes, carregarContas, carregarStatus]);

    /* ── editar e remover ── */
    const salvarConexao = async (r: RascunhoConexao) => {
        if (painel?.tipo !== 'conexao') return;
        const c = painel.conexao;
        setOcupado(true);
        try {
            const a = await ConexaoPlataformaService.atualizar({ conexaoPlataformaId: c.conexaoPlataformaId, nome: r.nome, status: r.status });
            if (!a.sucesso) { aviso.erro(a.mensagem || 'Não foi possívelatualizar a conexão.'); return; }
            if (r.usuarioId !== c.usuarioAtribuidoId) {
                const u = await ConexaoPlataformaService.atribuirUsuario(c.conexaoPlataformaId, r.usuarioId);
                if (!u.sucesso) { aviso.erro(u.mensagem || 'Não foi possívelatribuir o responsável.'); return; }
            }
            const agenteAntes = c.usandoAgentePadrao ? null : c.agenteIaId;
            if (r.agenteId !== agenteAntes) {
                const g = await ConexaoPlataformaService.atribuirAgenteIa(c.conexaoPlataformaId, r.agenteId);
                if (!g.sucesso) { aviso.erro(g.mensagem || 'Não foi possívelatribuir o agente de IA.'); return; }
            }
            aviso.ok('Conexão atualizada.');
            await carregarConexoes();
            setPainel(null);
        } catch {
            aviso.erro('Não foi possívelatualizar a conexão.');
        } finally {
            setOcupado(false);
        }
    };

    const removerConexao = async (c: ConexaoPlataforma) => {
        setOcupado(true);
        try {
            /* O canal via QR Code vive numa instância da Evolution Go: o endpoint dela
               apaga a instância E faz o soft delete da conexão, num passo só. */
            /* O RD desfaz o webhook e revoga o acesso do lado de lá antes de remover. */
            const r = c.plataforma === PlataformaEnum.WhatsappEvolution
                ? await EvolutionService.remover(c.conexaoPlataformaId)
                : c.plataforma === PlataformaEnum.RdStation
                    ? await RdStationService.desconectar()
                    : c.plataforma === PlataformaEnum.Webhook
                        ? await WebhookEntradaService.excluir(c.conexaoPlataformaId)
                        : await ConexaoPlataformaService.removerComPermissao(c.conexaoPlataformaId);
            if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possívelremover a conexão.'); return; }
            aviso.ok(`${c.nome} removido.`);
            setPainel(null);
            await Promise.all([carregarConexoes(), carregarStatus()]);
        } catch {
            aviso.erro('Não foi possívelremover a conexão.');
        } finally {
            setOcupado(false);
            setAConfirmar(null);
        }
    };

    const removerConta = async (conta: ContaAnuncio) => {
        setOcupado(true);
        try {
            const r = await TrafegoPagoService.removerConta(conta.contaAnuncioId);
            if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possívelremover a conta.'); return; }
            aviso.ok('Conta de anúncios removida.');
            setPainel(null);
            await carregarContas();
        } finally {
            setOcupado(false);
            setAConfirmar(null);
        }
    };

    /* Estável: o painel do webhook recarrega quando o onClose muda. */
    const fecharPainel = useCallback(() => setPainel(null), []);

    /* O RD e o webhook de entrada têm painel próprio; os canais de mensagem, o de sempre. */
    const abrirConexao = (c: ConexaoPlataforma) =>
        setPainel(c.plataforma === PlataformaEnum.RdStation ? { tipo: 'rd' }
            : c.plataforma === PlataformaEnum.Webhook ? { tipo: 'webhook', id: c.conexaoPlataformaId }
            : { tipo: 'conexao', conexao: c, modo: 'ver' });

    /* ── lista de canais ── */
    const canais = useMemo(
        () => conexoes.filter(c => c.plataforma !== PlataformaEnum.LeadAds || flag.leadAds),
        [conexoes, flag.leadAds]);

    const opcoesPlataforma = useMemo(() => {
        const m = new Map<string, number>();
        canais.forEach(c => { const k = getPlatformLabel(c.plataforma); m.set(k, (m.get(k) ?? 0) + 1); });
        return [...m].map(([valor, total]) => ({ valor, total }));
    }, [canais]);
    const opcoesStatus = useMemo(() => {
        const m = new Map<string, number>();
        canais.forEach(c => { const k = rotuloStatus(c.status); m.set(k, (m.get(k) ?? 0) + 1); });
        return [...m].map(([valor, total]) => ({ valor, total }));
    }, [canais]);

    const visiveis = useMemo(() => {
        const t = busca.trim().toLowerCase();
        let lista = canais.filter(c =>
            (!fPlataforma || getPlatformLabel(c.plataforma) === fPlataforma)
            && (!fStatus || rotuloStatus(c.status) === fStatus)
            && (!t || [c.nome, identificadorVisivel(c), c.usuarioAtribuidoNome, c.agenteIaNome, getPlatformLabel(c.plataforma)]
                .some(v => (v || '').toLowerCase().includes(t))));
        const semData = Number.MAX_SAFE_INTEGER;
        const exp = (c: ConexaoPlataforma) => { const d = diasAte(c.tokenExpiracao); return d === null ? semData : d; };
        if (!ordem) {
            /* Ordem natural: o que precisa de atenção primeiro — inativo, depois
               quem expira antes, depois o nome. */
            lista = [...lista].sort((a, b) =>
                Number(a.status === StatusConexaoEnum.Ativa) - Number(b.status === StatusConexaoEnum.Ativa)
                || exp(a) - exp(b)
                || a.nome.localeCompare(b.nome, 'pt-BR'));
        } else {
            const dir = ordem.direcao === 'asc' ? 1 : -1;
            lista = [...lista].sort((a, b) => ordem.coluna === 'canal'
                ? dir * a.nome.localeCompare(b.nome, 'pt-BR')
                : dir * (exp(a) - exp(b)));
        }
        return lista;
    }, [canais, busca, fPlataforma, fStatus, ordem]);

    const contasVisiveis = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return t ? contas.filter(c => [c.nome, c.contaExternaId].some(v => (v || '').toLowerCase().includes(t))) : contas;
    }, [contas, busca]);

    const ordenar = (coluna: 'canal' | 'expira') => setOrdem(o => {
        if (!o || o.coluna !== coluna) return { coluna, direcao: coluna === 'canal' ? 'asc' : 'asc' };
        return o.direcao === 'asc' ? { coluna, direcao: 'desc' } : null;
    });
    const setaDe = (coluna: 'canal' | 'expira') =>
        ordem?.coluna === coluna
            ? (ordem.direcao === 'asc' ? <LuChevronUp size={12} /> : <LuChevronDown size={12} />)
            : <LuChevronsUpDown size={12} />;

    /* ── as formas de conectar que EXISTEM para esta empresa ── */
    const formas = [
        { id: 'meta', mostrar: true, icone: <FaWhatsapp size={13} />, classe: 'is-wa',
          nome: 'WhatsApp · API oficial da Meta', apoio: `Parceiro oficial${usoTextoWa ? ` · ${usoTextoWa}` : ''}`,
          bloqueado: bloqueioWa,
          acao: () => setPainel({ tipo: 'meta' }) },
        { id: 'qr', mostrar: flag.qrCode, icone: <LuSmartphone size={13} />, classe: 'is-qr',
          nome: 'WhatsApp · Via QR Code', apoio: usoTextoWa ? `O celular precisa ficar ligado · ${usoTextoWa}` : 'O celular precisa ficar ligado e com internet',
          bloqueado: bloqueioWa,
          acao: () => setPainel({ tipo: 'qr' }) },
        /* Instagram: só pelo login do próprio Instagram. A disponibilidade se sabe no clique
           (a API avisa se faltar credencial no servidor). */
        { id: 'instagram', mostrar: flag.instagram, icone: <FaInstagram size={13} />, classe: 'is-ig',
          nome: 'Instagram', apoio: `Conta profissional do Instagram${usoTexto(PlataformaEnum.Instagram) ? ` · ${usoTexto(PlataformaEnum.Instagram)}` : ''}`,
          bloqueado: noLimite(PlataformaEnum.Instagram) ? 'Limite do plano atingido' : null,
          acao: () => abrirPopup('instagram') },
        /* Anúncios saiu daqui em 03/10: a porta da primeira conexão é a tela Apps.
           As contas já conectadas continuam na aba Contas de anúncio. */
    ].filter(f => f.mostrar);

    const conectando = conectandoMeta || conectandoIg || conectandoAds;
    const listaVazia = !carregando && canais.length === 0;

    const menuConectar = admin && !listaVazia && (
        <div className="cx-menu-wrap" ref={menuRef}>
            <button type="button" className="lk-btn is-primary" aria-haspopup="menu" aria-expanded={menuAberto}
                onClick={() => setMenuAberto(v => !v)} disabled={conectando}>
                <LuPlus size={14} /> {conectando ? 'Conectando…' : 'Conectar canal'} <LuChevronDown size={12} />
            </button>
            {menuAberto && (
                <div className="cx-menu" role="menu">
                    {formas.map((f, i) => (
                        <div key={f.id}>
                            {f.id === 'instagram' && i > 0 && <div className="cx-menu-sep" />}
                            <button type="button" role="menuitem" className="cx-mi" disabled={!!f.bloqueado}
                                title={f.bloqueado ?? undefined}
                                onClick={() => { setMenuAberto(false); f.acao(); }}>
                                <span className={`cx-bx is-sm ${f.classe}`} aria-hidden="true">{f.icone}</span>
                                <span className="cx-mi-t">
                                    <span className="cx-mi-n">{f.nome}</span>
                                    <span className="cx-mi-s">{f.bloqueado ?? f.apoio}</span>
                                </span>
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const total = aba === 'canais' ? canais.length : contas.length;

    /* Os avisos de "nada aqui" servem à tabela (computador) e aos cartões (celular). */
    const nadaNoFiltro = (
        <div className="lk-blank">
            <div className="lk-blank-title">Nada com esse filtro</div>
            <p className="lk-blank-text">Ajuste a busca ou limpe os filtros para ver os outros canais.</p>
        </div>
    );
    const nadaNasContas = (
        <div className="lk-blank">
            <div className="lk-blank-title">{contas.length === 0 ? 'Nenhuma conta de anúncios conectada' : 'Nada com esse termo'}</div>
            <p className="lk-blank-text">
                {contas.length === 0
                    ? (flag.anuncios && admin
                        ? 'A conta de anúncios da Meta se conecta pela tela Apps. Depois de conectada, ela aparece aqui.'
                        : flag.anuncios
                            ? 'Peça a um administrador para conectar a conta de anúncios da empresa.'
                            : 'A conexão de anúncios não está disponível para a sua empresa.')
                    : 'Ajuste a busca para ver as outras.'}
            </p>
            {contas.length === 0 && flag.anuncios && admin && (
                <button type="button" className="lk-btn is-primary" onClick={() => navigate('/apps')}>
                    <LuMegaphone size={14} /> Ir para Apps
                </button>
            )}
        </div>
    );

    /* ── celular: cartões no lugar das linhas ──
       O cartão é o `ListCard` do kit; aqui só se diz o que vai em cada vaga. */
    const cartaoCanal = (c: ConexaoPlataforma) => {
        if (aConfirmar?.tipo === 'conexao' && aConfirmar.id === c.conexaoPlataformaId) return (
            <CardConfirm key={c.conexaoPlataformaId}
                question={perguntaRemover(c)}
                confirmLabel="Remover" busy={ocupado}
                onCancel={() => setAConfirmar(null)} onConfirm={() => removerConexao(c)} />
        );
        const dias = renovaSozinha(c) ? null : diasAte(c.tokenExpiracao);
        const avisa = dias !== null && dias <= DIAS_AVISO_EXPIRACAO;
        const expira = renovaSozinha(c) ? null : textoExpiracao(c.tokenExpiracao, dias, avisa);
        return (
            <ListCard key={c.conexaoPlataformaId}
                avatar={<CanalIcone plataforma={c.plataforma} />}
                titulo={c.nome}
                apoio={<>{getPlatformLabel(c.plataforma)} · {identificadorVisivel(c)}</>}
                estado={
                    <>
                        <span className={`cx-st ${classeStatus(c.status)}`}>{rotuloStatus(c.status)}</span>
                        {expira && <span className={`cx-exp ${avisa ? 'is-warn' : ''}`}>· {expira}</span>}
                    </>
                }
                label={`Abrir ${c.nome}`}
                onOpen={() => abrirConexao(c)}
                acoes={admin && (
                    <RowAction icon={<LuTrash2 size={15} />} label="Remover conexão" danger
                        onClick={() => setAConfirmar({ tipo: 'conexao', id: c.conexaoPlataformaId })} />
                )}
            />
        );
    };
    const cartaoConta = (conta: ContaAnuncio) => {
        if (aConfirmar?.tipo === 'conta' && aConfirmar.id === conta.contaAnuncioId) return (
            <CardConfirm key={conta.contaAnuncioId}
                question={`Remover a conta ${conta.nome}? O gasto dela deixa de ser lido.`}
                confirmLabel="Remover" busy={ocupado}
                onCancel={() => setAConfirmar(null)} onConfirm={() => removerConta(conta)} />
        );
        const st = STATUS_SINCRONIZACAO[conta.statusSincronizacao] ?? { rotulo: conta.statusSincronizacao, classe: 'is-off', dica: '' };
        return (
            <ListCard key={conta.contaAnuncioId}
                avatar={<span className="cx-bx is-ads" aria-hidden="true"><LuMegaphone size={14} /></span>}
                titulo={conta.nome}
                apoio={<>{conta.contaExternaId} · {conta.moeda}</>}
                estado={
                    <>
                        <span className={`cx-st ${st.classe}`}>{st.rotulo}</span>
                        <span>· sincronizada {relativo(conta.ultimaSincronizacaoEm)}</span>
                    </>
                }
                label={`Abrir ${conta.nome}`}
                onOpen={() => setPainel({ tipo: 'conta', conta })}
                acoes={admin && (
                    <RowAction icon={<LuTrash2 size={15} />} label="Remover conta de anúncios" danger
                        onClick={() => setAConfirmar({ tipo: 'conta', id: conta.contaAnuncioId })} />
                )}
            />
        );
    };
    const esqueletos = Array.from({ length: 3 }).map((_, i) => <div key={i} className="lk-skeleton" />);

    return (
        <div className="page-shell lk-screen cx-screen">
            <PageHeader
                title="Conexões"
                subtitle="Os canais por onde as mensagens entram e as contas de anúncio da empresa."
                actions={menuConectar || undefined}
                tabs={[
                    { id: 'canais', label: 'Canais', count: carregando ? undefined : canais.length },
                    { id: 'anuncios', label: 'Contas de anúncio', count: carregando ? undefined : contas.length },
                ]}
                activeTab={aba}
                onTabChange={id => { setAba(id as Aba); setAConfirmar(null); }}
            />

            <div className="page-body lk-fill">
                {!(aba === 'canais' && listaVazia) && (
                    <div className="lk-filters">
                        <label className="lk-search">
                            <LuSearch size={14} />
                            <input type="search" placeholder="Buscar por nome ou identificador…"
                                value={busca} onChange={e => setBusca(e.target.value)} />
                        </label>
                        {aba === 'canais' && (
                            <>
                                <FiltroPill rotulo="Plataforma" valor={fPlataforma} opcoes={opcoesPlataforma} onChange={setFPlataforma} />
                                <FiltroPill rotulo="Status" valor={fStatus} opcoes={opcoesStatus} onChange={setFStatus} />
                            </>
                        )}
                        <span className="lk-count">
                            {aba === 'canais'
                                ? <>{total} {total === 1 ? 'canal' : 'canais'}{limite !== null && <> · {limite} por plataforma no plano</>}</>
                                : <>{total} {total === 1 ? 'conta' : 'contas'}{contas.length > 0 && <> · sincronizadas {relativo(contas.map(c => c.ultimaSincronizacaoEm).filter(Boolean).sort().at(-1) ?? null)}</>}</>}
                        </span>
                    </div>
                )}

                {aba === 'canais' && (
                    listaVazia ? (
                        /* Lista vazia: a porta é a do aviso, uma por forma de conectar (sem
                           tabela vazia nem botão no cabeçalho). Ver docs: "Porta explícita". */
                        <div className="lk-blank cx-blank">
                            <span className="cx-blank-ico" aria-hidden="true"><LuPlug size={20} /></span>
                            <div className="lk-blank-title">Nenhum canal conectado ainda</div>
                            <p className="lk-blank-text">
                                {admin
                                    ? 'Conecte o WhatsApp da empresa para as conversas começarem a entrar no Chat. O Instagram pode vir depois; anúncios e outras integrações ficam em Apps.'
                                    : 'Peça a um administrador da empresa para conectar o WhatsApp — as conversas passam a entrar no Chat.'}
                            </p>
                            {admin && (
                                <div className="cx-blank-acts">
                                    {formas.map((f, i) => (
                                        <button key={f.id} type="button" className={`lk-btn ${i === 0 ? 'is-primary' : ''}`}
                                            disabled={!!f.bloqueado} title={f.bloqueado ?? undefined} onClick={f.acao}>
                                            {f.icone} {f.nome.replace(' da Meta', '')}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : celular ? (
                        <ListCards>
                            {carregando ? esqueletos : visiveis.map(cartaoCanal)}
                            {!carregando && visiveis.length === 0 && nadaNoFiltro}
                        </ListCards>
                    ) : (
                        <div className="lk-table">
                            <div className="lk-scroll">
                                <div className="lk-row" style={{ gridTemplateColumns: COLUNAS_CANAIS }}>
                                    <button type="button" className={`lk-head lk-head-sort ${ordem?.coluna === 'canal' ? 'is-on' : ''}`}
                                        onClick={() => ordenar('canal')} aria-sort={ordem?.coluna === 'canal' ? (ordem.direcao === 'asc' ? 'ascending' : 'descending') : undefined}>
                                        <span>Canal</span>{setaDe('canal')}
                                    </button>
                                    <div className="lk-head">Responsável</div>
                                    <div className="lk-head">Agente de IA</div>
                                    <button type="button" className={`lk-head lk-head-sort ${ordem?.coluna === 'expira' ? 'is-on' : ''}`}
                                        onClick={() => ordenar('expira')} aria-sort={ordem?.coluna === 'expira' ? (ordem.direcao === 'asc' ? 'ascending' : 'descending') : undefined}>
                                        <span>Expira em</span>{setaDe('expira')}
                                    </button>
                                    <div className="lk-head">Status</div>
                                    <div className="lk-head lk-head-acts">Ações</div>
                                </div>
                                <div className="lk-body">
                                    {carregando
                                        ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                        : visiveis.map(c => {
                                            if (aConfirmar?.tipo === 'conexao' && aConfirmar.id === c.conexaoPlataformaId) return (
                                                <RowConfirm key={c.conexaoPlataformaId}
                                                    question={perguntaRemover(c)}
                                                    confirmLabel="Remover" busy={ocupado}
                                                    onCancel={() => setAConfirmar(null)}
                                                    onConfirm={() => removerConexao(c)} />
                                            );
                                            const dias = renovaSozinha(c) ? null : diasAte(c.tokenExpiracao);
                                            const avisa = dias !== null && dias <= DIAS_AVISO_EXPIRACAO;
                                            const atende = temAtendimento(c);
                                            const agente = c.usandoAgentePadrao
                                                ? <>{agentes.find(a => a.isDefault)?.nomeAgente ?? 'Agente'} <span className="lk-muted">· padrão</span></>
                                                : (c.agenteIaNome || agentes.find(a => a.id === c.agenteIaId)?.nomeAgente || 'Agente específico');
                                            return (
                                                <ListRow key={c.conexaoPlataformaId} columns={COLUNAS_CANAIS}
                                                    selected={(painel?.tipo === 'conexao' && painel.conexao.conexaoPlataformaId === c.conexaoPlataformaId)
                                                        || (painel?.tipo === 'rd' && c.plataforma === PlataformaEnum.RdStation)
                                                        || (painel?.tipo === 'webhook' && painel.id === c.conexaoPlataformaId)}>
                                                    <span className="lk-cell">
                                                        <span className="cx-canal">
                                                            <CanalIcone plataforma={c.plataforma} />
                                                            <span className="cx-canal-t">
                                                                <span className="cx-canal-n">{c.nome}</span>
                                                                <span className="cx-canal-s">{getPlatformLabel(c.plataforma)} · {identificadorVisivel(c)}</span>
                                                            </span>
                                                        </span>
                                                    </span>
                                                    <span className="lk-cell">
                                                        {!atende ? <span className="lk-empty-value">não se aplica</span>
                                                            : c.usuarioAtribuidoNome ? <span>{c.usuarioAtribuidoNome}</span>
                                                            : <span className="lk-tag">Toda a equipe</span>}
                                                    </span>
                                                    <span className="lk-cell">
                                                        {!atende ? <span className="lk-empty-value">não se aplica</span> : <span>{agente}</span>}
                                                    </span>
                                                    <span className="lk-cell">
                                                        {renovaSozinha(c)
                                                            ? <span className="lk-empty-value">renova sozinha</span>
                                                            : c.tokenExpiracao
                                                            ? <span className={`cx-exp ${avisa ? 'is-warn' : ''}`} title={avisa && dias !== null ? (dias < 0 ? 'Token vencido' : `Vence em ${dias} dia(s)`) : undefined}>{formatarData(c.tokenExpiracao)}</span>
                                                            : <span className="lk-empty-value">não expira</span>}
                                                    </span>
                                                    <span className="lk-cell">
                                                        <span className={`cx-st ${classeStatus(c.status)}`}>{rotuloStatus(c.status)}</span>
                                                    </span>
                                                    <RowActions>
                                                        <RowAction icon={<LuEye size={14} />} label="Ver conexão"
                                                            onClick={() => abrirConexao(c)} />
                                                        {admin && (
                                                            <RowAction icon={<LuTrash2 size={14} />} label="Remover conexão" danger
                                                                onClick={() => setAConfirmar({ tipo: 'conexao', id: c.conexaoPlataformaId })} />
                                                        )}
                                                    </RowActions>
                                                </ListRow>
                                            );
                                        })}
                                </div>
                            </div>
                            {!carregando && visiveis.length === 0 && nadaNoFiltro}
                        </div>
                    )
                )}

                {aba === 'anuncios' && (celular ? (
                    <ListCards>
                        {carregando ? esqueletos : contasVisiveis.map(cartaoConta)}
                        {!carregando && contasVisiveis.length === 0 && nadaNasContas}
                    </ListCards>
                ) : (
                    <div className="lk-table">
                        <div className="lk-scroll">
                            <div className="lk-row" style={{ gridTemplateColumns: COLUNAS_CONTAS }}>
                                <div className="lk-head">Conta</div>
                                <div className="lk-head">Moeda</div>
                                <div className="lk-head">Fuso horário</div>
                                <div className="lk-head">Última sincronização</div>
                                <div className="lk-head">Status</div>
                                <div className="lk-head lk-head-acts">Ações</div>
                            </div>
                            <div className="lk-body">
                                {carregando
                                    ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                    : contasVisiveis.map(conta => {
                                        if (aConfirmar?.tipo === 'conta' && aConfirmar.id === conta.contaAnuncioId) return (
                                            <RowConfirm key={conta.contaAnuncioId}
                                                question={`Remover a conta ${conta.nome}? O gasto dela deixa de ser lido.`}
                                                confirmLabel="Remover" busy={ocupado}
                                                onCancel={() => setAConfirmar(null)}
                                                onConfirm={() => removerConta(conta)} />
                                        );
                                        const st = STATUS_SINCRONIZACAO[conta.statusSincronizacao] ?? { rotulo: conta.statusSincronizacao, classe: 'is-off', dica: '' };
                                        return (
                                            <ListRow key={conta.contaAnuncioId} columns={COLUNAS_CONTAS}
                                                selected={painel?.tipo === 'conta' && painel.conta.contaAnuncioId === conta.contaAnuncioId}>
                                                <span className="lk-cell">
                                                    <span className="cx-canal">
                                                        <span className="cx-bx is-ads" aria-hidden="true"><LuMegaphone size={14} /></span>
                                                        <span className="cx-canal-t">
                                                            <span className="cx-canal-n">{conta.nome}</span>
                                                            <span className="cx-canal-s">{conta.contaExternaId}</span>
                                                        </span>
                                                    </span>
                                                </span>
                                                <span className="lk-cell"><span>{conta.moeda}</span></span>
                                                <span className="lk-cell lk-muted"><span>{conta.fusoHorario}</span></span>
                                                <span className="lk-cell lk-muted"><span>{relativo(conta.ultimaSincronizacaoEm)}</span></span>
                                                <span className="lk-cell"><span className={`cx-st ${st.classe}`} title={st.dica}>{st.rotulo}</span></span>
                                                <RowActions>
                                                    <RowAction icon={<LuEye size={14} />} label="Ver conta" onClick={() => setPainel({ tipo: 'conta', conta })} />
                                                    {admin && (
                                                        <RowAction icon={<LuTrash2 size={14} />} label="Remover conta de anúncios" danger
                                                            onClick={() => setAConfirmar({ tipo: 'conta', id: conta.contaAnuncioId })} />
                                                    )}
                                                </RowActions>
                                            </ListRow>
                                        );
                                    })}
                            </div>
                        </div>
                        {!carregando && contasVisiveis.length === 0 && nadaNasContas}
                    </div>
                ))}
            </div>

            {/* ── painéis ── */}
            <ConexaoPanel
                conexao={painel?.tipo === 'conexao' ? painel.conexao : null}
                modo={painel?.tipo === 'conexao' ? painel.modo : 'ver'}
                onModo={m => setPainel(p => (p?.tipo === 'conexao' ? { ...p, modo: m } : p))}
                onClose={() => setPainel(null)}
                admin={admin}
                multiContas={statusWa.multiWhatsappHabilitado}
                usuarios={usuarios}
                agentes={agentes}
                carregandoOpcoes={carregandoOpcoes}
                salvando={ocupado}
                onSalvar={salvarConexao}
                onRemover={async () => { if (painel?.tipo === 'conexao') await removerConexao(painel.conexao); }}
                onReconectarQr={() => {
                    if (painel?.tipo !== 'conexao') return;
                    setPainel({ tipo: 'qr', conexaoId: painel.conexao.conexaoPlataformaId, nome: painel.conexao.nome });
                }}
                onDesconectarNumero={async () => {
                    if (painel?.tipo !== 'conexao') return;
                    setOcupado(true);
                    try {
                        const r = await EvolutionService.logout(painel.conexao.conexaoPlataformaId);
                        if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possíveldesconectar o número.'); return; }
                        aviso.ok(r.mensagem || 'Número desconectado.');
                        setPainel(null);
                        await Promise.all([carregarConexoes(), carregarStatus()]);
                    } catch {
                        aviso.erro('Não foi possíveldesconectar o número.');
                    } finally {
                        setOcupado(false);
                    }
                }}
            />

            <RecordPanel
                open={painel?.tipo === 'conta'}
                onClose={() => setPainel(null)}
                initials="AD"
                squareAvatar
                title={painel?.tipo === 'conta' ? painel.conta.nome : ''}
                subtitle="Conta de anúncios da Meta"
            >
                {painel?.tipo === 'conta' && (() => {
                    const st = STATUS_SINCRONIZACAO[painel.conta.statusSincronizacao] ?? { rotulo: painel.conta.statusSincronizacao, classe: 'is-off', dica: '' };
                    return (
                        <PanelSection title="Conta">
                            <div className="rp-kv cx-kv">
                                <span className="rp-k">Identificador</span><span className="rp-v cx-mono">{painel.conta.contaExternaId}</span>
                                <span className="rp-k">Moeda</span><span className="rp-v">{painel.conta.moeda}</span>
                                <span className="rp-k">Fuso horário</span><span className="rp-v">{painel.conta.fusoHorario}</span>
                                <span className="rp-k">Sincronização</span>
                                <span className="rp-v"><span className={`cx-st ${st.classe}`}>{st.rotulo}</span>{st.dica && <> · <span className="lk-muted">{st.dica}</span></>}</span>
                                <span className="rp-k">Última leitura</span><span className="rp-v"><LuRefreshCw size={12} /> {relativo(painel.conta.ultimaSincronizacaoEm)}</span>
                                {painel.conta.ultimoErro && (<><span className="rp-k">Último erro</span><span className="rp-v cx-warn">{painel.conta.ultimoErro}</span></>)}
                            </div>
                        </PanelSection>
                    );
                })()}
            </RecordPanel>

            <ConectarMetaPanel
                open={painel?.tipo === 'meta'}
                onClose={() => setPainel(null)}
                onConectar={conectarMeta}
                conectando={conectandoMeta}
                pronto={fbPronto}
                uso={{ usado: usadasWa, limite: limiteWa }}
            />

            <RdStationPanel
                open={painel?.tipo === 'rd'}
                onClose={() => setPainel(null)}
                admin={admin}
                usuarios={usuarios}
                onMudou={carregarConexoes}
            />

            <WebhookEntradaPanel
                entradaId={painel?.tipo === 'webhook' ? painel.id : null}
                onClose={fecharPainel}
                admin={admin}
                usuarios={usuarios}
                onMudou={carregarConexoes}
            />

            <ConectarQrPanel
                open={painel?.tipo === 'qr'}
                onClose={() => { setPainel(null); carregarConexoes(); }}
                conexaoId={painel?.tipo === 'qr' ? painel.conexaoId ?? null : null}
                nome={painel?.tipo === 'qr' ? painel.nome : undefined}
                onConectado={() => { aviso.ok('WhatsApp conectado.'); carregarConexoes(); carregarStatus(); }}
            />
        </div>
    );
};

export default ConnectionsPage;
